import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  companiesManagementService,
  kopToRubSmart,
  kopToRubFull,
  STATUS_COLORS,
  type Company,
  type CompanyModuleAnalytics,
  type CompanyModuleAnalyticsItem,
  type CompanyBalanceWidget,
  type CompanyRecruiterStat,
} from '../../services/companiesManagementService';
import { AddCompanyUserModal } from './AddCompanyUserModal';
import { EditCompanyUserModal } from './EditCompanyUserModal';

/** Делит копейки на целую часть и дробную («,96») для раздельной стилизации (как в виджетах списка). */
function splitRub(kopecks: number): { sign: string; int: string; frac: string } {
  const sign = kopecks < 0 ? '-' : '';
  const abs = Math.abs(kopecks);
  return {
    sign,
    int: Math.trunc(abs / 100).toLocaleString('ru-RU'),
    frac: String(abs % 100).padStart(2, '0'),
  };
}

/**
 * Карточка одной компании (вид суперадмина), открывается по клику из списка компаний.
 * Строго по макету Figma: «Назад к списку компаний» + шапка + баланс ₽ + карточки услуг +
 * тулбар (Показатели/Финансы, поиск, «Добавить пользователя») + таблица по сотрудникам.
 *
 * Данные: шапка — getCompany; баланс — getCompanyBalance (агрегат по лимитам); карточки услуг —
 * getModuleAnalytics; таблица — getCompanyRecruiters (вакансии/Интервью/Одобрено — business-back;
 * диалоги/Ответы % — olivia-back; Ср. время — длительность интервью).
 */

interface Props {
  company: Company;
  onBack: () => void;
}

// ─── Иконки ─────────────────────────────────────────────────────────────────

const ArrowLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
    <path d="M8.87 9.25H18v1.5H8.87l4.02 4.02-1.06 1.06L6 10l5.83-5.83 1.06 1.06L8.87 9.25Z" fill="#E16349" />
  </svg>
);

// team-line — иконка компании (как в списке/макете)
const TeamIcon = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" className="shrink-0">
    <path
      d="M20 18a7.5 7.5 0 0 1 7.5 7.5v9h-3v-9a4.5 4.5 0 0 0-4.5-4.5 4.5 4.5 0 0 0-4.5 4.5v9h-3v-9A7.5 7.5 0 0 1 20 18Zm-9.75 4.5c.42 0 .82.05 1.21.14a8.94 8.94 0 0 0-.46 2.86v9h-3v-6.75a3.75 3.75 0 0 1 2.25-5.25Zm19.5 0a3.75 3.75 0 0 1 1.5 7.18V34.5h-3v-9c0-1-.16-1.96-.46-2.86.39-.09.79-.14 1.21-.14ZM10.25 13.5a3.75 3.75 0 1 1 0 7.5 3.75 3.75 0 0 1 0-7.5Zm19.5 0a3.75 3.75 0 1 1 0 7.5 3.75 3.75 0 0 1 0-7.5ZM20 4.5a6 6 0 1 1 0 12 6 6 0 0 1 0-12Zm0 3a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z"
      fill="#E16349"
    />
  </svg>
);

// settings (шестерёнка) — классический cog, как в макете
const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#989286" className="shrink-0">
    <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.48.48 0 0 0-.48-.41h-3.84a.48.48 0 0 0-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.74 8.87a.49.49 0 0 0 .12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 0 0-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32a.49.49 0 0 0-.12-.61l-2.01-1.58ZM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2Z" />
  </svg>
);

const CopyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="#989286" className="shrink-0">
    <path d="M6.67 4.5V3.42c0-.2.08-.39.22-.53.14-.14.33-.22.53-.22h9c.2 0 .39.08.53.22.14.14.22.33.22.53v10.5c0 .2-.08.39-.22.53-.14.14-.33.22-.53.22h-1.08v1.08c0 .42-.34.75-.76.75H5.09a.75.75 0 0 1-.75-.75l.002-10.5c0-.41.34-.75.75-.75H6.67ZM5.42 6v9h7.5V6h-7.5ZM8.42 4.5h6V13h1.5V3.75h-7.5V4.5Z" />
  </svg>
);

const CrownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="#989286" className="shrink-0">
    <path d="M2 5.2 4.5 7 8 2.5 11.5 7 14 5.2 13 12.5H3L2 5.2Zm6 4.8a1.2 1.2 0 1 0 0-2.4 1.2 1.2 0 0 0 0 2.4Z" />
  </svg>
);

const UserIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="#989286" className="shrink-0">
    <circle cx="7" cy="4.2" r="2.4" />
    <path d="M2.2 11.5c0-2.4 2.1-3.8 4.8-3.8s4.8 1.4 4.8 3.8v.3H2.2v-.3Z" />
  </svg>
);

const WalletIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" className="shrink-0">
    <path
      d="M16.6 11.4v-3a.9.9 0 0 0-.9-.9H4.3a1.34 1.34 0 0 1 0-2.69h13.5a.9.9 0 0 1 .9.9v3M18.7 13.5h-3a1.34 1.34 0 0 0 0 2.7h3a.9.9 0 0 0 .9-.9v-.9a.9.9 0 0 0-.9-.9Z"
      stroke="#161922"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.4 6.5v8.4c0 .5.4.9.9.9h13.5a.9.9 0 0 0 .9-.9V13.5"
      stroke="#161922"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// RemixIcon ri-file-list-3-line (как в макете) — Скоринг резюме
const ScoringIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#161922" className="shrink-0">
    <path d="M20 22H4a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1zM19 20V4H5v16h14zM8 7h8v2H8V7zm0 4h8v2H8v-2zm0 4h5v2H8v-2z" />
  </svg>
);

// RemixIcon ri-chat-3-line — Диалоги с ИИ
const DialogIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#161922" className="shrink-0">
    <path d="M5.763 17H20V5H4v13.385L5.763 17zm.692 2L2 22.5V4a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H6.455z" />
  </svg>
);

// RemixIcon ri-user-follow-line (пользователь + проверка) — AI-Скрининг
const ScreeningIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="#161922" className="shrink-0">
    <path d="M14 14.252v2.09A6 6 0 0 0 6 22l-2-.001a8 8 0 0 1 10-7.748zM12 13c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6zm0-2c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm6.586 4.001 3.536 3.535-1.415 1.415-3.535-3.536-1.414 1.415v-4.243h4.243l-.95.95z" />
  </svg>
);

// ─── Карточка услуги ──────────────────────────────────────────────────────────

const MODULE_META: Record<string, { label: string; Icon: React.FC }> = {
  SEARCH: { label: 'Скоринг резюме', Icon: ScoringIcon },
  DIALOG: { label: 'Диалоги с ИИ', Icon: DialogIcon },
  INTERVIEW: { label: 'AI-Скрининг', Icon: ScreeningIcon },
};
const MODULE_ORDER = ['SEARCH', 'DIALOG', 'INTERVIEW'];

/** Тариф клиента (₽/шт) из копеек: 100 → «1», 1500 → «15», 8000 → «80». */
function tariffRub(kopecks: number): string {
  const rub = kopecks / 100;
  return (rub % 1 === 0 ? rub.toFixed(0) : rub.toFixed(2).replace(/0$/, '')).toLocaleString();
}

interface ServiceCardProps {
  moduleName: string;
  Icon: React.FC;
  units: number;
  revenueKopecks: number;
  tariffKopecks: number;
}

const ServiceStatCard: React.FC<ServiceCardProps> = ({ moduleName, Icon, units, revenueKopecks, tariffKopecks }) => (
  <div className="flex-1 min-w-0 h-[172px] bg-white border border-[#F4F5F0] rounded-[16px] p-5 flex flex-col justify-between gap-4">
    {/* Заголовок: иконка + название | справа количество «шт.» */}
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-[10px] min-w-0">
        <span className="flex items-start justify-center w-[26px] h-[26px] bg-[#F4F5F0] rounded-[6px] shrink-0 pt-1.5">
          <Icon />
        </span>
        <span className="text-[14px] font-semibold text-[#161922] tracking-[-0.15px] truncate">{moduleName}</span>
      </div>
      <span className="text-[14px] font-medium text-[#989286] tracking-[-0.234px] whitespace-nowrap shrink-0">
        {units.toLocaleString('ru-RU')} шт.
      </span>
    </div>
    {/* Низ: крупная стоимость ₽ (копейки мельче и серым) | бейдж тариф ₽/шт */}
    <div className="flex items-end justify-between gap-3">
      <span className="flex items-baseline">
        {(() => {
          const { sign, int, frac } = splitRub(revenueKopecks);
          return (
            <>
              <span className="text-[36px] font-semibold leading-[1.3] tracking-[0.07px] text-[#161922]">{sign}{int}</span>
              <span className="text-[24px] font-semibold leading-[1.3] text-[#989286]">,{frac}₽</span>
            </>
          );
        })()}
      </span>
      <span className="flex items-center justify-center px-2 py-1 bg-[#F4F5F0] rounded-[8px] shrink-0">
        <span className="text-[14px] font-medium text-[#989286] tracking-[-0.234px] whitespace-nowrap">
          {tariffRub(tariffKopecks)} ₽/шт
        </span>
      </span>
    </div>
  </div>
);

// ─── Тулбар и таблица сотрудников ─────────────────────────────────────────────

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
    <circle cx="9" cy="9" r="5.5" stroke="#525866" strokeWidth="1.5" />
    <path d="M13.5 13.5L17 17" stroke="#525866" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
    <path d="M10 4.75V15.25M4.75 10H15.25" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// ri-pencil-line — редактирование сотрудника (REL-256)
const EditPencilIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#525866" className="shrink-0">
    <path d="M15.728 9.686l-1.414-1.414L5 17.586V19h1.414l9.314-9.314zm1.414-1.414l1.414-1.414-1.414-1.414-1.414 1.414 1.414 1.414zM7.242 21H3v-4.243L16.435 3.322a1 1 0 0 1 1.414 0l2.829 2.829a1 1 0 0 1 0 1.414L7.243 21z" />
  </svg>
);

const QuestionIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
    <circle cx="8" cy="8" r="6" stroke="#CDD0D5" strokeWidth="1.2" />
    <path d="M6.6 6.4c0-.8.66-1.4 1.4-1.4s1.4.6 1.4 1.35c0 .55-.33.85-.8 1.15-.5.32-.6.55-.6 1.1M8 11.2h.01" stroke="#CDD0D5" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const ROLE_LABEL: Record<string, string> = {
  SUPER_ADMIN: 'Суперадмин',
  OWNER: 'Владелец',
  ADMIN: 'Администратор',
  RECRUITER: 'Рекрутёр',
};

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** Заголовок числовой колонки (по правому краю), опц. с иконкой-вопросом как в макете. */
const ColHead: React.FC<{ label: string; hint?: boolean }> = ({ label, hint }) => (
  <div className="flex-1 min-w-[110px] px-6 flex items-center justify-end gap-1 text-[13px] leading-5 font-semibold uppercase tracking-[0.573px] text-[#6B7280]">
    <span>{label}</span>
    {hint && <QuestionIcon />}
  </div>
);

const RecruiterRow: React.FC<{ r: CompanyRecruiterStat; onEdit: () => void }> = ({ r, onEdit }) => (
  <div className="group relative flex items-stretch min-h-[90px] border-b border-black/[0.08] last:border-b-0 hover:bg-[#fafafa] transition-colors">
    {/* Сотрудник */}
    <div className="flex-[1.6] min-w-[220px] flex items-center gap-4 px-6 py-4">
      <div className="relative shrink-0">
        <div className="w-10 h-10 rounded-full bg-[#F4F5F0] flex items-center justify-center text-[14px] font-medium text-[#161922]">
          {initialsOf(r.name)}
        </div>
        {r.online && (
          <span className="absolute -top-0.5 right-0 w-2 h-2 rounded-full bg-[#38C793] border border-black/20" style={{ boxShadow: '0 0 0 2px #FAFAFA' }} />
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[15px] font-medium leading-[22px] tracking-[-0.234px] text-[#161922] truncate">{r.name}</div>
        {r.email && <div className="text-[13px] leading-5 tracking-[-0.076px] text-[#989286] truncate">{r.email}</div>}
      </div>
    </div>
    {/* Роль */}
    <div className="w-[130px] shrink-0 flex items-center px-6 text-[13px] leading-5 tracking-[-0.076px] text-[#989286]">
      {ROLE_LABEL[r.role ?? ''] ?? r.role ?? '—'}
    </div>
    {/* Числовые колонки (выровнены вправо) */}
    {[
      r.positions.toLocaleString('ru-RU'),
      r.dialogs.toLocaleString('ru-RU'),
      `${Math.round(r.responseRate)}%`,
      `${Math.round(r.interviewRate)}%`,
      `${Math.round(r.approvedRate)}%`,
      `${Math.round(r.avgTimeMinutes)} мин`,
    ].map((v, i) => (
      <div key={i} className="flex-1 min-w-[110px] flex items-center justify-end px-6 text-[15px] font-medium leading-[22px] tracking-[-0.234px] text-[#161922]">
        {v}
      </div>
    ))}
    {/* Редактирование сотрудника (REL-256): иконка появляется при наведении на строку */}
    <button
      type="button"
      onClick={onEdit}
      title="Редактировать пользователя"
      aria-label="Редактировать пользователя"
      className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-[8px] bg-white border border-[#F4F5F0] opacity-0 group-hover:opacity-100 hover:bg-[#f0f0f0] transition-opacity"
    >
      <EditPencilIcon />
    </button>
  </div>
);

// ─── Компонент карточки компании ──────────────────────────────────────────────

export const CompanyCardContent: React.FC<Props> = ({ company, onBack }) => {
  // Полные данные компании (список мог не вернуть ownerName/description/maxAccounts) — догружаем.
  const [full, setFull] = useState<Company | null>(null);
  const [analytics, setAnalytics] = useState<CompanyModuleAnalytics | null>(null);
  const [balance, setBalance] = useState<CompanyBalanceWidget | null>(null);

  // Низ карточки: вкладка «Показатели/Финансы», поиск, таблица сотрудников.
  const [tab, setTab] = useState<'metrics' | 'finance'>('metrics');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [recruiters, setRecruiters] = useState<CompanyRecruiterStat[]>([]);
  const [recLoading, setRecLoading] = useState(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Модалки добавления/редактирования пользователя (REL-256) + триггер перезагрузки данных
  // карточки (таблица сотрудников + счётчик аккаунтов) после мутации, без F5.
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey(k => k + 1), []);

  const onSearch = useCallback((v: string) => {
    setSearch(v);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setDebounced(v), 350);
  }, []);

  useEffect(() => {
    if (company.id == null) return;
    let cancelled = false;
    setRecLoading(true);
    companiesManagementService
      .getCompanyRecruiters(company.id, { search: debounced || undefined, page: 0, size: 200 })
      .then(res => { if (!cancelled) setRecruiters(res.content ?? []); })
      .catch(() => { if (!cancelled) setRecruiters([]); })
      .finally(() => { if (!cancelled) setRecLoading(false); });
    return () => { cancelled = true; };
  }, [company.id, debounced, reloadKey]);

  useEffect(() => {
    if (company.id == null) return;
    let cancelled = false;
    companiesManagementService
      .getCompany(company.id)
      .then(d => { if (!cancelled) setFull(d); })
      .catch(() => { /* останутся данные из строки списка */ });
    companiesManagementService
      .getModuleAnalytics(company.id)
      .then(d => { if (!cancelled) setAnalytics(d); })
      .catch(() => { /* карточки услуг просто не покажутся */ });
    companiesManagementService
      .getCompanyBalance(company.id)
      .then(d => { if (!cancelled) setBalance(d); })
      .catch(() => { /* под-цифры баланса не покажутся, останется крупное число */ });
    return () => { cancelled = true; };
  }, [company.id, reloadKey]);

  // Предпочитаем полные данные, но только если ответ — валидная компания (есть name);
  // иначе оставляем строку из списка (защита от неожиданного формата/ошибки бэкенда).
  const c = full && full.name ? full : company;
  const description = c.description || analytics?.companyDescription || '';
  const dotColor = STATUS_COLORS[c.status] ?? '#38C793';

  // Услуги строго в порядке макета (Скоринг → Диалоги → Скрининг)
  const modules = MODULE_ORDER
    .map(code => analytics?.modules.find((m: CompanyModuleAnalyticsItem) => m.module === code))
    .filter((m): m is CompanyModuleAnalyticsItem => Boolean(m));

  return (
    <div className="bg-[#f5f6f1] rounded-[24px] w-full h-full overflow-hidden flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto py-6">
       <div className="w-full max-w-[1224px] mx-auto flex flex-col gap-5">

        {/* Назад к списку компаний */}
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1 self-start px-0 py-2.5 rounded-[10px] shrink-0"
        >
          <ArrowLeftIcon />
          <span className="px-1 text-[14px] font-medium leading-5 tracking-[-0.006em] text-[#E16349]">
            Назад к списку компаний
          </span>
        </button>

        {/* Заголовок страницы */}
        <h1 className="text-[20px] font-semibold leading-[130%] tracking-[0.0703px] text-[#161922] shrink-0">
          Статистика и лимиты
        </h1>

        {/* Шапка компании */}
        <div className="flex items-start justify-between gap-6 shrink-0">
          <div className="flex items-center gap-[18px] min-w-0">
            {/* Иконка компании + статус-точка */}
            <div className="relative w-16 h-16 shrink-0">
              <div className="w-16 h-16 bg-white rounded-[12px] flex items-center justify-center">
                <TeamIcon />
              </div>
              <span
                className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border border-black/20 box-border"
                style={{ backgroundColor: dotColor, boxShadow: '0 0 0 3px #F5F6F1' }}
              />
            </div>

            <div className="flex flex-col gap-0.5 min-w-0">
              {/* Имя + настройки */}
              <div className="flex items-center gap-1">
                <span className="text-[24px] font-medium leading-8 tracking-[-0.015em] text-[#161922] truncate">
                  {c.name}
                </span>
                <SettingsIcon />
              </div>
              {/* e-mail · контакт · аккаунты */}
              <div className="flex items-center gap-2 text-[#989286]">
                {c.ownerEmail && (
                  <span className="flex items-center gap-1 min-w-0">
                    <CopyIcon />
                    <span className="text-[14px] font-medium leading-[22px] tracking-[-0.234px] truncate">
                      {c.ownerEmail}
                    </span>
                  </span>
                )}
                {c.ownerName && (
                  <>
                    <span className="w-px h-[15px] bg-[#E9EAE2] shrink-0" />
                    <span className="flex items-center gap-1 min-w-0">
                      <CrownIcon />
                      <span className="text-[14px] font-medium leading-[22px] tracking-[-0.234px] truncate">
                        {c.ownerName}
                      </span>
                    </span>
                  </>
                )}
                {c.accountsCount !== undefined && (
                  <>
                    <span className="w-px h-[15px] bg-[#E9EAE2] shrink-0" />
                    <span className="flex items-center gap-0.5 shrink-0">
                      <UserIcon />
                      <span className="text-[15px] font-medium leading-[22px] tracking-[-0.234px]">
                        {c.accountsCount}{c.maxAccounts != null ? `/${c.maxAccounts}` : ''}
                      </span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Описание компании */}
          {description && (
            <div className="w-[440px] shrink-0 bg-[#E9E9E5] border border-[#F4F5F0] rounded-[12px] px-5 py-3">
              <p className="text-[12px] font-medium leading-4 text-[#6B7280]">{description}</p>
            </div>
          )}
        </div>

        {/* Баланс: крупное = «можно распределить» (доступно) + серое «/ всего выделялось за период» */}
        <div className="bg-white border border-[#F4F5F0] rounded-[16px] px-6 py-5 flex items-center gap-4 shrink-0">
          <span className="flex items-center justify-center w-10 h-10 bg-[#F4F5F0] rounded-[9px] shrink-0">
            <WalletIcon />
          </span>
          <div className="flex items-baseline gap-1.5">
            {(() => {
              // Большое число — «можно распределить» (доступно); до загрузки — баланс как плейсхолдер.
              const { sign, int, frac } = splitRub(
                balance ? balance.availableToDistributeKopecks : (c.balanceKopecks ?? 0),
              );
              return (
                <>
                  <span className="text-[36px] font-semibold leading-[1.3] tracking-[0.07px] text-[#161922]">{sign}{int},{frac}</span>
                  <span className="text-[24px] font-semibold leading-[1.3] tracking-[0.07px] text-[#989286] whitespace-nowrap">
                    {balance ? `/ ${kopToRubSmart(balance.totalToppedUpKopecks)} ` : ''}₽
                  </span>
                </>
              );
            })()}
          </div>

          {balance && (
            <div className="flex items-center gap-8 ml-auto shrink-0">
              <div className="flex flex-col gap-1">
                <span className="text-[14px] font-medium leading-[22px] tracking-[-0.234px] text-[#989286]">Выдано лимитами</span>
                <span className="text-[20px] font-bold leading-[30px] tracking-[-0.449px] text-[#161922]">{kopToRubSmart(balance.reservedByLimitsKopecks)} ₽</span>
              </div>
              <div className="w-px h-[57px] bg-[#E9EAE2]" />
              <div className="flex flex-col gap-1">
                <span className="text-[14px] font-medium leading-[22px] tracking-[-0.234px] text-[#989286]">Траты без лимитов</span>
                <span className="text-[20px] font-bold leading-[30px] tracking-[-0.449px] text-[#161922]">{kopToRubSmart(balance.spentByUnlimitedKopecks)} ₽</span>
              </div>
            </div>
          )}
        </div>

        {/* Карточки услуг */}
        {modules.length > 0 && (
          <div className="flex gap-5 shrink-0">
            {modules.map(m => {
              const meta = MODULE_META[m.module] ?? { label: m.moduleName, Icon: ScoringIcon };
              return (
                <ServiceStatCard
                  key={m.module}
                  moduleName={meta.label}
                  Icon={meta.Icon}
                  units={m.units}
                  revenueKopecks={m.revenueKopecks}
                  tariffKopecks={m.tariffKopecks}
                />
              );
            })}
          </div>
        )}

        {/* Тулбар: переключатель «Показатели/Финансы» + поиск + кнопка */}
        <div className="flex items-center gap-5 shrink-0">
          <div className="flex items-center gap-1 p-0.5 bg-black/5 rounded-[12px] shrink-0">
            {([['metrics', 'Показатели'], ['finance', 'Финансы']] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-3 py-2 rounded-[10px] text-[14px] font-medium leading-5 tracking-[-0.006em] transition-colors ${
                  tab === key
                    ? 'bg-white text-[#56584F] shadow-[0px_6px_10px_rgba(27,28,29,0.06),0px_2px_4px_rgba(27,28,29,0.02)]'
                    : 'text-[#868C98]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 bg-white border border-[#EBE7DC] rounded-[10px] px-2 py-2.5 flex-1 min-w-0">
            <SearchIcon />
            <input
              type="text"
              placeholder="Поиск по компании или email"
              value={search}
              onChange={e => onSearch(e.target.value)}
              className="flex-1 min-w-0 bg-transparent text-[14px] leading-5 tracking-[-0.006em] text-[#525866] placeholder:text-[#525866] outline-none"
            />
          </div>

          <button
            type="button"
            onClick={() => setAddUserOpen(true)}
            className="flex items-center gap-1 px-2.5 py-2.5 rounded-[10px] bg-[#E16349] hover:bg-[#c8513a] text-white text-[14px] font-medium leading-5 tracking-[-0.006em] transition-colors whitespace-nowrap shrink-0 shadow-[0px_1px_2px_rgba(55,93,251,0.08)]"
          >
            <PlusIcon /> Добавить пользователя
          </button>
        </div>

        {/* Контент вкладки */}
        {tab === 'metrics' ? (
          <div className="bg-[#FAFAFA] rounded-[16px] border border-[#F4F5F0] overflow-hidden flex-1 min-h-0 flex flex-col">
            {/* Шапка таблицы */}
            <div className="flex items-center h-12 border-b border-black/[0.08] shrink-0">
              <div className="flex-[1.6] min-w-[220px] flex items-center pl-6 pr-3 text-[13px] leading-5 font-semibold uppercase tracking-[0.573px] text-[#6B7280]">Сотрудник</div>
              <div className="w-[130px] shrink-0 flex items-center px-6 text-[13px] leading-5 font-semibold uppercase tracking-[0.573px] text-[#6B7280]">Роль</div>
              <ColHead label="вакансий" />
              <ColHead label="диалогов" />
              <ColHead label="Ответы" hint />
              <ColHead label="Интервью" hint />
              <ColHead label="Одобрено" />
              <ColHead label="Ср. время" hint />
            </div>
            {/* Строки */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {recLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#E16349]" />
                </div>
              ) : recruiters.length === 0 ? (
                <div className="flex items-center justify-center py-14 text-[13px] text-[#989286]">Сотрудники не найдены</div>
              ) : (
                recruiters.map(r => (
                  <RecruiterRow key={r.userId} r={r} onEdit={() => setEditUserId(r.userId)} />
                ))
              )}
            </div>
          </div>
        ) : (
          <FinanceView analytics={analytics} />
        )}
       </div>
      </div>

      {/* Модалки добавления/редактирования пользователя (REL-256) */}
      {addUserOpen && (
        <AddCompanyUserModal
          companyId={c.id!}
          companyName={c.name}
          onClose={() => setAddUserOpen(false)}
          onSaved={() => { setAddUserOpen(false); reload(); }}
        />
      )}
      {editUserId != null && (
        <EditCompanyUserModal
          userId={editUserId}
          onClose={() => setEditUserId(null)}
          onSaved={() => { setEditUserId(null); reload(); }}
        />
      )}
    </div>
  );
};

// ─── Вкладка «Финансы» (агрегаты из module-analytics: тариф/себестоимость/маржа) ────────

const FinanceView: React.FC<{ analytics: CompanyModuleAnalytics | null }> = ({ analytics }) => {
  if (!analytics) {
    return <div className="flex items-center justify-center py-14 text-[13px] text-[#989286]">Нет данных по финансам</div>;
  }
  const profit = analytics.revenueKopecks - analytics.costKopecks;
  const avgMargin = analytics.revenueKopecks > 0 ? (profit / analytics.revenueKopecks) * 100 : 0;
  const marginPct = (tariff: number, cost: number) => (tariff > 0 ? ((tariff - cost) / tariff) * 100 : 0);
  return (
    <div className="flex flex-col gap-5 shrink-0">
      {/* Соотношение цен и маржи */}
      <div className="bg-white rounded-[16px] border border-[#F4F5F0] overflow-hidden">
        {analytics.modules.map(m => (
          <div key={m.module} className="flex items-center justify-between px-6 py-4 gap-4 border-b border-[#F4F5F0] last:border-b-0">
            <div className="text-[14px] font-medium text-[#161922] flex-1 min-w-0 truncate">{m.moduleName}</div>
            <div className="text-right w-[130px]">
              <div className="text-[11px] text-[#989286]">Тариф клиента</div>
              <div className="text-[14px] font-semibold text-[#161922]">{kopToRubFull(m.tariffKopecks)} ₽/шт</div>
            </div>
            <div className="text-right w-[130px]">
              <div className="text-[11px] text-[#989286]">Себестоимость</div>
              <div className="text-[14px] font-semibold text-[#E16349]">{kopToRubFull(m.costPerUnitKopecks)} ₽/шт</div>
            </div>
            <div className="text-right w-[80px]">
              <div className="text-[11px] text-[#989286]">Маржа</div>
              <div className="text-[14px] font-semibold text-[#16a34a]">{marginPct(m.tariffKopecks, m.costPerUnitKopecks).toFixed(1)}%</div>
            </div>
          </div>
        ))}
      </div>
      {/* Финансовая эффективность */}
      <div className="grid grid-cols-4 gap-4 bg-white rounded-[16px] border border-[#F4F5F0] p-6">
        {[
          ['Выручено', `${kopToRubFull(analytics.revenueKopecks)} ₽`, '#161922'],
          ['Себестоимость', `${kopToRubFull(analytics.costKopecks)} ₽`, '#E16349'],
          ['Ср. маржа', `${avgMargin.toFixed(1)}%`, '#16a34a'],
          ['Прибыль', `+${kopToRubFull(profit)} ₽`, '#16a34a'],
        ].map(([label, val, color]) => (
          <div key={label}>
            <div className="text-[12px] text-[#989286] mb-1">{label}</div>
            <div className="text-[18px] font-bold" style={{ color }}>{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CompanyCardContent;
